import { http } from "./http";

/**
 * IPPBX → Extensions: every agent with a PBX login, its SIP registration
 * state (dbo.PbxEvent), call stats, the per-agent log and actions.
 * Admins see everything, managers only their own customers.
 */

export type RegistrationState = "registered" | "unregistered" | "expired" | "never";

export interface PbxExtension {
  agentId: number;
  customerId: number;
  customerName: string;
  customerEnabled: boolean;
  managerId: number;
  managerName: string;
  agentRef: string;
  /** 4-digit extension number colleagues dial / transfer to (script 20); null when not assigned. */
  pbxExtension: string | null;
  agentName: string;
  statusId: number;
  statusName: string;
  phoneEnabled: boolean;
  recordCalls: boolean;
  maxChannels: number;
  pbxUsername: string | null;
  pbxIdentityMode: "denied" | "hidden" | "number" | "from";
  pbxIdentityDnisId: number | null;
  pbxIdentityNumber: string | null;
  pbxTimeoutSeconds: number | null;
  activePbxId: number | null;
  pbxHostname: string | null;
  registered: boolean;
  registrationState: RegistrationState;
  lastRegisterUtc: string | null;
  secondsSinceRegister: number | null;
  ipAddress: string | null;
  userAgent: string | null;
  lastRegisterHostname: string | null;
  lastEventUtc: string | null;
  lastEventType: string | null;
  /** sofia::unregister events in the last 24 h (registration flaps). */
  unregisters24h: number;
  /** Calls handled by the extension since UTC midnight. */
  callsTodayIn: number;
  /** Calls dialled from the extension since UTC midnight; null until script 16 is in place. */
  callsTodayOut: number | null;
  talkSecondsToday: number;
  activeCalls: number;
  lastCallUtc: string | null;
}

export interface PbxExtensionSummary {
  total: number;
  registered: number;
  unregistered: number;
  never: number;
  onCall: number;
}

export interface PbxExtensionList {
  items: PbxExtension[];
  summary: PbxExtensionSummary;
  generatedUtc: string;
}

export interface PbxExtensionDetail {
  extension: PbxExtension;
  customerTimeZone: string | null;
  customerTimeZoneIana: string;
  outboundTrackingAvailable: boolean;
  /** CustomerSetting 'PbxInternalCalls': extensions of this customer may call and transfer to each other. */
  internalCallsEnabled: boolean;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PbxEvent {
  pbxEventId: number;
  timestampUtc: string;
  eventType: string;
  hostname: string;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface PbxAgentLog {
  statsAgentLogId: number;
  logDatetimeUtc: string;
  logTypeId: number;
  logTypeName: string;
  logTypeDescription: string;
  infoString: string | null;
  infoInt: number | null;
  callId: string | null;
  channelTypeId: number | null;
  channelTypeName: string | null;
}

export interface PbxCall {
  statsAgentSessionId: number | null;
  callId: string | null;
  callIdOut: string | null;
  /** Dialled from the extension (tracked through dbo.PbxOutboundCall). */
  outbound: boolean;
  channelTypeId: number | null;
  channelTypeName: string | null;
  startUtc: string;
  endUtc: string | null;
  duration: number | null;
  dnisId: number | null;
  dnisPresentation: string | null;
  dialledNumber: string | null;
  presentedNumber: string | null;
  cdrId: number | null;
  direction: string | null;
  callingNumber: string | null;
  calledNumber: string | null;
  inviteUtc: string | null;
  acceptUtc: string | null;
  disconnectUtc: string | null;
  ringSeconds: number | null;
  hangupCause: string | null;
  /** SUCCESS / NO_ANSWER / ORIGINATOR_CANCEL / …, same rule as the CDR report. */
  hangupCauseNormalised: string | null;
  ipRemote: string | null;
  hasRecording: boolean;
}

export interface PbxCustomer {
  customerId: number;
  customerName: string;
  managerName: string;
  extensions: number;
}

export interface PbxLogType {
  logTypeId: number;
  name: string;
  description: string;
}

export interface ReregisterResult {
  requestId: number;
  requestedAtUtc: string;
  alreadyPending: boolean;
  pbxUsername: string;
}

const base = "/api/ippbx/extensions";

function qs(params: Record<string, string | number | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const pbxExtensionsApi = {
  list: (customerId: number | null, signal?: AbortSignal) =>
    http<PbxExtensionList>(`${base}${qs({ customerId })}`, { signal }),
  get: (agentId: number, signal?: AbortSignal) => http<PbxExtensionDetail>(`${base}/${agentId}`, { signal }),
  events: (agentId: number, p: { eventType?: "register" | "unregister" | null; page: number; pageSize?: number }, signal?: AbortSignal) =>
    http<Paged<PbxEvent>>(`${base}/${agentId}/events${qs(p)}`, { signal }),
  log: (agentId: number, p: { from: string; to: string; logTypeId?: number | null; page: number; pageSize?: number }) =>
    http<Paged<PbxAgentLog>>(`${base}/${agentId}/log${qs(p)}`),
  calls: (agentId: number, p: { from: string; to: string; page: number; pageSize?: number }) =>
    http<Paged<PbxCall>>(`${base}/${agentId}/calls${qs(p)}`),
  logTypes: () => http<PbxLogType[]>(`${base}/logtypes`),
  customers: () => http<PbxCustomer[]>(`${base}/customers`),
  /** Same-origin recording URL for one of the extension's calls. */
  callAudioUrl: (agentId: number, callId: string, download = false) =>
    `${base}/${agentId}/calls/${encodeURIComponent(callId)}/audio${download ? "?download=true" : ""}`,
  reregister: (agentId: number) => http<ReregisterResult>(`${base}/${agentId}/reregister`, { method: "POST" }),
};
